import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Download, Gift, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";
import { UserEditDialog } from "@/components/admin/UserEditDialog";

const PLAN = { a2g_annual: "Annuale", a2g_monthly: "Mensile", annual: "Annuale", monthly: "Mensile", omaggio: "Omaggio" };
export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const subLabel = (u) => (u.free_access ? "Omaggio" : u.subscription_active ? `Attivo · ${PLAN[u.plan] || u.plan || ""}` : "Non attivo");

function exportCsv(users) {
  const head = ["Nome", "Email", "Telefono", "Iscritto il", "Ultimo accesso", "Abbonamento", "Codice sconto", "Profilo", "Giorni completati"];
  const rows = users.map((u) => [u.name, u.email, u.phone, fmtDate(u.created_at), fmtDate(u.last_seen_at), subLabel(u), u.promo_code || "", u.profile_title || "", `${u.days_done}/7`]);
  const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  a.download = `iscritti-coach2go-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  toast.success("Elenco scaricato");
}

function UserRow({ u, onOpen }) {
  const active = u.subscription_active;
  return (
    <button onClick={onOpen} data-testid={`admin-user-${u.id}`} className="flex w-full items-center gap-3 rounded-2xl border border-[#27272A] bg-[#121215] p-4 text-left transition-colors hover:border-[#D4AF37]/50">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-semibold text-[#FAF9F6]">{u.name || "—"}</p>
          {u.role === "admin" && <span className="rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-[11px] font-semibold text-[#E5C158]">ADMIN</span>}
          {u.ghost && <span data-testid={`admin-user-ghost-${u.id}`} className="rounded-full bg-[#A78BFA]/15 px-2 py-0.5 text-[11px] font-semibold text-[#C4B5FD]">GHOST · non conteggiato</span>}
        </div>
        <p className="truncate text-sm text-[#A1A1AA]">{u.email}</p>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[12px]">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${active ? "bg-[#86EFAC]/12 text-[#86EFAC]" : "bg-[#27272A] text-[#A1A1AA]"}`}>
            {u.free_access && <Gift size={12} />} {subLabel(u)}
          </span>
          {u.promo_code && <span className="rounded-full bg-[#D4AF37]/12 px-2 py-0.5 text-[#E5C158]">{u.promo_code}</span>}
          {u.profile_title && <span className="rounded-full bg-[#27272A] px-2 py-0.5 text-[#D4D4D8]">{u.profile_title} · {u.days_done}/7</span>}
        </div>
        <p className="mt-2 text-[12px] text-[#71717A]">Iscritto {fmtDate(u.created_at)} · Ultimo accesso {fmtDate(u.last_seen_at)}</p>
      </div>
      <ChevronRight size={18} className="shrink-0 text-[#52525B]" />
    </button>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data.users)).catch(() => toast.error("Impossibile caricare gli iscritti"));
  useEffect(() => { load(); }, []);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (users || []).filter((u) => !s || `${u.name} ${u.email} ${u.phone}`.toLowerCase().includes(s));
  }, [users, q]);

  if (!users) return <Loader />;
  const real = users.filter((u) => !u.ghost);
  const activeCount = real.filter((u) => u.subscription_active).length;

  return (
    <AppShell title="Iscritti" greeting="Area admin">
      <div className="px-5 pt-5 pb-10">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#A1A1AA]" data-testid="admin-users-count"><b className="text-[#FAF9F6]">{real.length}</b> iscritti · <b className="text-[#86EFAC]">{activeCount}</b> con accesso attivo</p>
          <button onClick={() => exportCsv(list)} data-testid="admin-users-export" className="btn-outline-gold inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm"><Download size={15} /> Excel</button>
        </div>
        <div className="relative mt-4">
          <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email o telefono" data-testid="admin-users-search" className="w-full rounded-xl border border-[#27272A] bg-[#09090B] py-3 pl-10 pr-4 text-base text-[#FAF9F6] outline-none focus:border-[#D4AF37]" />
        </div>
        <div className="mt-4 space-y-2.5" data-testid="admin-users-list">
          {list.map((u) => <UserRow key={u.id} u={u} onOpen={() => setSel(u)} />)}
          {!list.length && <p className="py-10 text-center text-[#71717A]">Nessun risultato</p>}
        </div>
      </div>
      <UserEditDialog user={sel} onClose={() => setSel(null)} onChanged={() => { setSel(null); load(); }} />
    </AppShell>
  );
}
